<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import ThemeButton from '@/components/ThemeButton.vue'
import { postMessageToRN } from '@/bridge'
import type { VersionData } from '@/composables/useVersionCheck'

defineOptions({ name: 'NewVersionModal' })

// ---- Props / Emits ----
const props = defineProps<{
  visible: boolean
  versionData: VersionData | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

// ---- i18n ----
const { t } = useI18n()

// ---- Callbacks ----
/** 点击「去更新」：通知 RN 触发版本更新（传递版本号与下载链接） */
function handleUpdate() {
  const data = props.versionData
  if (!data) return
  postMessageToRN({ type: 'update-version', params: { version: data.version, url: data.url } })
}

/** 点击左侧按钮：强制更新时退出应用，否则仅关闭弹窗 */
function handleSecondary() {
  // 强制更新模式：通知 RN 退出应用
  if (props.versionData?.strategy) {
    postMessageToRN({ type: 'exitApp' })
    return
  }
  emit('close')
}

/** 点击遮罩：强制更新时忽略，否则关闭弹窗 */
function handleMaskClick() {
  if (props.versionData?.strategy) return
  emit('close')
}
</script>

<template>
  <!-- 版本更新弹窗：固定全屏遮罩 + 居中卡片；强制更新时遮罩不可点关 -->
  <div
    v-if="visible && versionData"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(1,0,14,0.5)]"
    @click="handleMaskClick"
  >
    <!-- 卡片容器：阻止冒泡，避免点击内部触发遮罩关闭 -->
    <div class="relative w-[327px]" @click.stop>
      <!-- 火箭图标：悬浮在卡片右上 -->
      <div class="absolute z-10 top-[-30px] right-2.5 pointer-events-none">
        <svg width="98" height="147" viewBox="0 0 98 147" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="paint0_linear_rocket" x1="39.0966" y1="124.505" x2="77.713" y2="67.7859" gradientUnits="userSpaceOnUse">
              <stop stop-color="white" stop-opacity="0" />
              <stop offset="1" stop-color="#41CC8B" />
            </linearGradient>
            <linearGradient id="paint1_linear_rocket" x1="39.0966" y1="124.505" x2="77.713" y2="67.7859" gradientUnits="userSpaceOnUse">
              <stop stop-color="white" stop-opacity="0" />
              <stop offset="1" stop-color="#41CC8B" />
            </linearGradient>
            <linearGradient id="paint2_linear_rocket" x1="11.2897" y1="108.712" x2="60.1307" y2="60.5157" gradientUnits="userSpaceOnUse">
              <stop stop-color="white" stop-opacity="0" />
              <stop offset="1" stop-color="#41CC8B" />
            </linearGradient>
            <linearGradient id="paint3_linear_rocket" x1="42.2852" y1="62.4873" x2="53.7049" y2="60.4294" gradientUnits="userSpaceOnUse">
              <stop stop-color="#CBE1FF" />
              <stop offset="0.273946" stop-color="#77B0FF" />
              <stop offset="0.995282" stop-color="#207DFF" />
            </linearGradient>
            <linearGradient id="paint4_linear_rocket" x1="81.2856" y1="34.7373" x2="77.8607" y2="47.7698" gradientUnits="userSpaceOnUse">
              <stop stop-color="#CBE1FF" />
              <stop offset="0.273946" stop-color="#77B0FF" />
              <stop offset="0.995282" stop-color="#207DFF" />
            </linearGradient>
            <linearGradient id="paint5_linear_rocket" x1="40.0352" y1="50.4873" x2="68.5352" y2="17.4873" gradientUnits="userSpaceOnUse">
              <stop offset="0.485087" stop-color="#3388FD" />
              <stop offset="0.969275" stop-color="#D0E4FF" />
            </linearGradient>
            <linearGradient id="paint6_linear_rocket" x1="79.0713" y1="53.2434" x2="37.3448" y2="39.4878" gradientUnits="userSpaceOnUse">
              <stop stop-color="#CBE1FF" />
              <stop offset="0.273946" stop-color="#77B0FF" />
              <stop offset="0.995282" stop-color="#207DFF" />
            </linearGradient>
            <linearGradient id="paint7_linear_rocket" x1="28.0814" y1="0.676684" x2="28.7516" y2="14.0395" gradientUnits="userSpaceOnUse">
              <stop stop-color="#7FF1BC" />
              <stop offset="1" stop-color="#41CC8B" />
            </linearGradient>
            <linearGradient id="paint8_linear_rocket" x1="68.5283" y1="43.1636" x2="56.9953" y2="42.5531" gradientUnits="userSpaceOnUse">
              <stop stop-color="#CBE1FF" />
              <stop offset="0.273946" stop-color="#77B0FF" />
              <stop offset="0.995282" stop-color="#207DFF" />
            </linearGradient>
            <mask id="mask0_rocket" maskUnits="userSpaceOnUse" x="0" y="52" width="97" height="95">
              <path
                d="M0 91.291C13.5434 92.6123 27.0682 91.2807 38.2673 89.0506C54.6776 85.7827 71.5536 71.8388 62.2721 57.9165L68.8004 52.041C70.7589 53.2379 76.1774 56.872 82.1834 61.8335C98.5042 78.4806 97.5443 104.718 95.5098 111.582C92.2549 122.562 76.5098 138.582 60.5098 146.123L33.5098 131.291C29.8104 126.286 14.8845 114.793 0 91.291Z"
                fill="url(#paint1_linear_rocket)"
              />
            </mask>
            <mask id="mask1_rocket" maskUnits="userSpaceOnUse" x="24" y="0" width="57" height="69">
              <path
                d="M27.325 2.35145C26.483 6.47735 24.8206 14.7062 24.6918 15.9853C24.5188 17.7042 24.3803 22.4826 25.211 27.8454C26.0417 33.2083 34.2102 42.7995 38.1906 46.9247L58.6343 66.8897C59.9599 68.1842 62.0009 68.399 63.5668 67.4088L78.3935 58.0335C80.0573 56.9814 80.7083 54.8753 79.9284 53.0679L76.6101 45.3778C73.8411 39.5337 66.9533 25.4734 61.5538 15.9853C56.1543 6.49727 47.1898 3.09394 43.3824 2.57828L30.1782 0.392534C28.8538 0.173291 27.5935 1.03609 27.325 2.35145Z"
                fill="#C4E0FF"
              />
            </mask>
          </defs>

          <!-- 底部气流尾迹 -->
          <path
            d="M0 91.291C13.5434 92.6123 27.0682 91.2807 38.2673 89.0506C54.6776 85.7827 71.5536 71.8388 62.2721 57.9165L68.8004 52.041C70.7589 53.2379 76.1774 56.872 82.1834 61.8335C98.5042 78.4806 98.4475 105.054 95.5098 111.582C85.2034 134.485 76.5827 138.548 60.7116 146.028L60.5098 146.123L33.5098 131.291C29.8104 126.286 14.8845 114.793 0 91.291Z"
            fill="url(#paint0_linear_rocket)"
            fill-opacity="0.36"
          />
          <g mask="url(#mask0_rocket)">
            <path
              d="M-20.6583 68.574C-7.62486 72.4853 5.90247 73.7901 17.3215 73.7642C34.054 73.7263 53.3047 63.3031 46.8859 47.8507L54.4257 43.3463C56.1163 44.8987 60.7312 49.5106 65.6662 55.5384C78.4657 75.0235 73.2795 101.086 69.1365 106.924C54.602 127.406 45.3591 129.728 28.3425 134.003L28.126 134.057L4.4978 114.291C1.8344 108.666 -10.5916 94.5075 -20.6583 68.574Z"
              fill="url(#paint2_linear_rocket)"
              fill-opacity="0.36"
            />
          </g>

          <!-- 火箭主体 -->
          <path
            d="M27.325 2.35145C26.483 6.47735 24.8206 14.7062 24.6918 15.9853C24.5188 17.7042 24.3803 22.4826 25.211 27.8454C26.0417 33.2083 34.2102 42.7995 38.1906 46.9247L58.6343 66.8897C59.9599 68.1842 62.0009 68.399 63.5668 67.4088L78.3935 58.0335C80.0573 56.9814 80.7083 54.8753 79.9284 53.0679L76.6101 45.3778C73.8411 39.5337 66.9533 25.4734 61.5538 15.9853C56.1543 6.49727 47.1898 3.09394 43.3824 2.57828L30.1782 0.392534C28.8538 0.173291 27.5935 1.03609 27.325 2.35145Z"
            fill="#C4E0FF"
            fill-opacity="0.6"
          />
          <g>
            <path
              d="M43.6988 64.7674C40.8111 59.8171 43.5269 54.4542 45.2458 52.3916L58.6527 63.736L54.0996 77.3951C53.8446 78.1603 52.8378 78.3229 52.3618 77.6717C49.9864 74.4217 46.1416 68.9549 43.6988 64.7674Z"
              fill="url(#paint3_linear_rocket)"
            />
          </g>
          <g>
            <path
              d="M83.4226 37.0486C79.6537 32.7312 73.7044 33.5922 71.2009 34.5623L77.6697 50.89L92.0675 50.9376C92.8742 50.9402 93.3498 50.0381 92.8846 49.379C90.5634 46.0902 86.6108 40.7008 83.4226 37.0486Z"
              fill="url(#paint4_linear_rocket)"
            />
          </g>
          <g>
            <path
              d="M27.325 2.35145C26.483 6.47735 24.8206 14.7062 24.6918 15.9853C24.5188 17.7042 24.3803 22.4826 25.211 27.8454C26.0417 33.2083 34.2102 42.7995 38.1906 46.9247L58.6343 66.8897C59.9599 68.1842 62.0009 68.399 63.5668 67.4088L78.3935 58.0335C80.0573 56.9814 80.7083 54.8753 79.9284 53.0679L76.6101 45.3778C73.8411 39.5337 66.9533 25.4734 61.5538 15.9853C56.1543 6.49727 47.1898 3.09394 43.3824 2.57828L30.1782 0.392534C28.8538 0.173291 27.5935 1.03609 27.325 2.35145Z"
              fill="url(#paint5_linear_rocket)"
            />
          </g>
          <g mask="url(#mask1_rocket)">
            <g>
              <path
                d="M81.7549 55.5615L79.558 50.5342C74.8699 57.2315 68.1517 62.243 60.3961 64.8282L56.8691 66.0039L60.9944 69.0978L65.1196 70.1291L81.7549 55.5615Z"
                fill="url(#paint6_linear_rocket)"
              />
            </g>
            <ellipse cx="28.3135" cy="0.917531" rx="18.1421" ry="13.2118" transform="rotate(-30, 28.3135, 0.917531)" fill="url(#paint7_linear_rocket)" />
            <g>
              <ellipse cx="66.0717" cy="54.2001" rx="3.09394" ry="15.2547" transform="rotate(-34.825, 66.0717, 54.2001)" fill="#007DF2" />
            </g>
          </g>
          <g>
            <ellipse cx="66.0717" cy="54.2001" rx="3.09394" ry="15.2547" transform="rotate(-34.825, 66.0717, 54.2001)" fill="url(#paint8_linear_rocket)" />
          </g>
          <g>
            <circle cx="45.5258" cy="24.5454" r="7.21919" fill="#66A6FF" />
          </g>
          <circle cx="45.5258" cy="24.5454" r="6.46919" stroke="#ECF4FF" stroke-width="1.5" />
        </svg>
      </div>

      <!-- 卡片主体：浅绿渐变背景 -->
      <div class="rounded-3xl overflow-hidden bg-[linear-gradient(180deg,#E6FFF3_0%,#FFFFFF_58%)]">
        <!-- 内容区 -->
        <div class="px-6 pt-6">
          <!-- 标题 -->
          <div class="pb-6">
            <h2 class="text-xl font-medium leading-7 text-ink/90">
              {{ t('newVersionFound') }} V{{ versionData.version }}
            </h2>
          </div>

          <!-- 更新内容列表 -->
          <div v-if="versionData.changelog && versionData.changelog.length > 0" class="flex flex-col gap-2 pb-6">
            <p class="text-sm font-medium leading-[22.4px] text-ink/90">{{ t('updateContent') }}</p>
            <!-- 日志条目：逐条编号，超长滚动 -->
            <div class="max-h-[200px] overflow-y-auto flex flex-col gap-1">
              <p
                v-for="(item, index) in versionData.changelog"
                :key="index"
                class="text-sm font-normal leading-[19.6px] text-ink/50"
              >
                {{ index + 1 }}、{{ item.trim() }}
              </p>
            </div>
          </div>
        </div>

        <!-- 按钮区：强制更新→退出/去更新；普通→稍后更新/去更新 -->
        <div class="flex gap-3 px-6 pb-6">
          <ThemeButton variant="secondary" block class="flex-1 min-w-0" @click="handleSecondary">
            {{ versionData.strategy ? t('exit') : t('updateLater') }}
          </ThemeButton>
          <ThemeButton block class="flex-1 min-w-0" @click="handleUpdate">
            {{ t('goUpdate') }}
          </ThemeButton>
        </div>
      </div>
    </div>
  </div>
</template>
